import React, { useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material';
import { Line } from 'recharts';
import { ResponsiveContainer, LineChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from 'recharts';
import {
  formatValue,
  getLineColor,
  getLegendConfig,
  getYAxisDomain,
  processTimeseriesData,
  makeAxisFormatter,
} from './chartUtils';
import { parseThresholds } from './thresholdUtils';
import WidgetState from './WidgetState';

const LineChartWidget = ({ data, config = {} }) => {
  const theme = useTheme();

  // Memoize processed data to prevent unnecessary recalculations
  const timeseriesData = useMemo(() => {
    if (!data || !data.data || data.type !== 'timeseries') {
      return null;
    }
    return processTimeseriesData(data.data, config.legendTemplate);
  }, [data, config.legendTemplate]);

  // Memoize legend configuration
  const legendConfig = useMemo(() => getLegendConfig(config), [
    config
  ]);

  // Memoize Y-axis domain
  const yAxisDomain = useMemo(() => getYAxisDomain(config), [
    config
  ]);

  // Memoize Y-axis label
  const yAxisLabel = useMemo(() =>
    config.yAxisLabel ? { value: config.yAxisLabel, angle: -90, position: 'insideLeft' } : undefined,
    [config.yAxisLabel]
  );

  // Memoize formatters — timestamps are already in milliseconds from processTimeseriesData
  const xAxisTickFormatter = useCallback(
    (timestamp) => new Date(timestamp).toLocaleTimeString(),
    []
  );

  const yAxisTickFormatter = useMemo(
    () => makeAxisFormatter(config.unit, config.decimalPlaces),
    [config.unit, config.decimalPlaces]
  );

  const thresholds = useMemo(() => parseThresholds(config.thresholdsJson), [config.thresholdsJson]);

  const tooltipLabelFormatter = useCallback(
    (timestamp) => new Date(timestamp).toLocaleString(),
    []
  );

  const tooltipFormatter = useCallback(
    (value) => [formatValue(value, config), ''],
    [config]
  );

  // Theme-aware tooltip surface — glassy dark on dark, light bg + dark text on light.
  const tooltipContentStyle = useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    return {
      backgroundColor: isDark ? 'rgba(5, 5, 5, 0.75)' : 'rgba(255, 255, 255, 0.92)',
      backdropFilter: 'blur(12px)',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.08)',
      borderRadius: '8px',
      boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.15)',
      color: isDark ? '#fff' : '#1a1a1a',
    };
  }, [theme.palette.mode]);

  // Handle scalar/table results - show value with hint
  if (!timeseriesData && data) {
    let scalarValue = null;
    if (data.type === 'scalar') {
      scalarValue = parseFloat(data.data);
    } else if (data.type === 'table' && data.rows && data.rows.length > 0) {
      const numVal = Object.values(data.rows[0]).find(v => typeof v === 'number');
      if (numVal !== undefined) scalarValue = numVal;
    }

    if (scalarValue !== null) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>{formatValue(scalarValue, config)}</div>
          <div style={{ fontSize: '0.75rem', color: '#999', marginTop: 8 }}>
            Scalar result — use Stat or Gauge widget, or add GROUP BY with a time column for timeseries
          </div>
        </div>
      );
    }
    return (
      <WidgetState isEmpty />
    );
  }

  if (!timeseriesData) {
    return (
      <WidgetState isEmpty />
    );
  }

  // Honest empty state: an empty window (offline host, tight filter) must say
  // so — not render bare axes that read as "zero".
  if (!timeseriesData.dataPoints || timeseriesData.dataPoints.length === 0) {
    return (
      <WidgetState isEmpty />
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={timeseriesData.dataPoints} syncId="dashboard-sync">
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="timestamp"
          type="number"
          domain={config.timeDomain || ['dataMin', 'dataMax']}
          allowDataOverflow={Boolean(config.timeDomain)}
          tickFormatter={xAxisTickFormatter}
        />
        <YAxis
          domain={yAxisDomain}
          label={yAxisLabel}
          tickFormatter={yAxisTickFormatter}
        />
        <Tooltip
          labelFormatter={tooltipLabelFormatter}
          formatter={tooltipFormatter}
          contentStyle={tooltipContentStyle}
          itemStyle={{ fontWeight: 500 }}
          cursor={{ stroke: 'rgba(128, 128, 128, 0.25)', strokeWidth: 1 }}
        />
        {legendConfig && <Legend {...legendConfig} />}
        {thresholds.map((t, i) => (
          <ReferenceLine
            key={`th-${i}`}
            y={t.value}
            stroke={t.color}
            strokeDasharray="4 4"
            ifOverflow="extendDomain"
            label={t.label ? { value: t.label, position: 'right', fill: t.color, fontSize: 11 } : undefined}
          />
        ))}
        {timeseriesData.series.map((series, index) => (
          <Line
            key={series}
            type="monotone"
            dataKey={series}
            stroke={getLineColor(index, config.colorScheme)}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
            connectNulls={config.connectNulls || false}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(LineChartWidget, (prevProps, nextProps) => {
  // Custom comparison function for better performance
  return (
    prevProps.data === nextProps.data &&
    prevProps.config?.legendTemplate === nextProps.config?.legendTemplate &&
    prevProps.config?.showLegend === nextProps.config?.showLegend &&
    prevProps.config?.legendPosition === nextProps.config?.legendPosition &&
    prevProps.config?.unit === nextProps.config?.unit &&
    prevProps.config?.decimalPlaces === nextProps.config?.decimalPlaces &&
    prevProps.config?.useThousandSeparator === nextProps.config?.useThousandSeparator &&
    prevProps.config?.yAxisMin === nextProps.config?.yAxisMin &&
    prevProps.config?.yAxisMax === nextProps.config?.yAxisMax &&
    prevProps.config?.yAxisLabel === nextProps.config?.yAxisLabel &&
    prevProps.config?.colorScheme === nextProps.config?.colorScheme &&
    prevProps.config?.connectNulls === nextProps.config?.connectNulls &&
    prevProps.config?.thresholdsJson === nextProps.config?.thresholdsJson &&
    (prevProps.config?.timeDomain?.[0] === nextProps.config?.timeDomain?.[0] &&
     prevProps.config?.timeDomain?.[1] === nextProps.config?.timeDomain?.[1])
  );
});